/**
 * rag-collection-registry.ts
 *
 * Canonical URI Namespacing & Authorization for Scoped RAG Collections:
 * - Namespaces: global:*, plugin:<pluginId>:*, channel:<channelId>:*
 * - Validates collection ownership and prevents cross-domain leakage
 */

import {
  RagCollectionDefinition,
  RagCollectionBinding,
  GroundingPolicy,
} from '../channels/agent-channel-contracts.js';

export const DEFAULT_GROUNDING_POLICIES: Record<string, GroundingPolicy> = {
  shopping: {
    minimumSimilarity: 0.65,
    minimumEvidenceChunks: 1,
    requireCitationForClaims: true,
    allowUngroundedConversation: true,
  },
  wiki: {
    minimumSimilarity: 0.65,
    minimumEvidenceChunks: 1,
    requireCitationForClaims: true,
    allowUngroundedConversation: false,
  },
  scrum: {
    minimumSimilarity: 0.60,
    minimumEvidenceChunks: 1,
    requireCitationForClaims: true,
    allowUngroundedConversation: true,
  },
  crm: {
    minimumSimilarity: 0.65,
    minimumEvidenceChunks: 1,
    requireCitationForClaims: true,
    allowUngroundedConversation: true,
  },
  cms: {
    minimumSimilarity: 0.65,
    minimumEvidenceChunks: 1,
    requireCitationForClaims: true,
    allowUngroundedConversation: true,
  },
  advisor: {
    minimumSimilarity: 0.60,
    minimumEvidenceChunks: 1,
    requireCitationForClaims: true,
    allowUngroundedConversation: true,
  },
};

export class RagCollectionRegistry {
  private collections = new Map<string, RagCollectionDefinition>();
  private bindings = new Map<string, RagCollectionBinding>();

  constructor() {
    this.seedDefaultCollections();
  }

  private seedDefaultCollections(): void {
    const defaultDefs: RagCollectionDefinition[] = [
      {
        id: 'plugin:commerce:catalog',
        name: 'Commerce Product Catalog',
        scopeType: 'plugin',
        scopeId: 'commerce',
        description: 'Structured product specifications, pricing, and stock items',
        documentCount: 42,
        chunkCount: 156,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:commerce:policies',
        name: 'Commerce Shipping & Returns Policy',
        scopeType: 'plugin',
        scopeId: 'commerce',
        description: 'Shipping estimates, warranty rules, and return procedures',
        documentCount: 8,
        chunkCount: 24,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'global:documentation-all',
        name: 'Global Sovereign Knowledge Wiki',
        scopeType: 'global',
        description: 'Complete architecture manuals, deployment guides, and platform RFCs',
        documentCount: 120,
        chunkCount: 640,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:scrum-master:conventions',
        name: 'Engineering Agile Conventions',
        scopeType: 'plugin',
        scopeId: 'scrum-master',
        description: 'Definition of Done, story point sizing standards, and sprint cadence',
        documentCount: 15,
        chunkCount: 52,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:scrum-master:arch-rfcs',
        name: 'Architecture RFCs & ADRs',
        scopeType: 'plugin',
        scopeId: 'scrum-master',
        description: 'Microkernel architectural decision records and mesh protocols',
        documentCount: 35,
        chunkCount: 190,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:crm:consent',
        name: 'Customer Privacy & Consent Policies',
        scopeType: 'plugin',
        scopeId: 'crm',
        description: 'GDPR / local jurisdiction consent guidelines and communication limits',
        documentCount: 6,
        chunkCount: 18,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:crm:customer-policies',
        name: 'CRM SLA & Account Tiers',
        scopeType: 'plugin',
        scopeId: 'crm',
        description: 'Enterprise support SLA tiers and response time commitments',
        documentCount: 10,
        chunkCount: 30,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:cms:styleguide',
        name: 'Editorial Style Guide & Brand Voice',
        scopeType: 'plugin',
        scopeId: 'cms',
        description: 'Brand tone, formatting guidelines, and publishing standards',
        documentCount: 12,
        chunkCount: 44,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'plugin:cms:taxonomies',
        name: 'Content Categories & Taxonomies',
        scopeType: 'plugin',
        scopeId: 'cms',
        description: 'Tagging structure and SEO taxonomies for edge deployment',
        documentCount: 5,
        chunkCount: 15,
        lastIndexedAt: new Date().toISOString(),
      },
      {
        id: 'channel:advisor:cross-domain',
        name: 'Cross-Domain Strategic Planning Base',
        scopeType: 'channel',
        scopeId: 'advisor',
        description: 'High-level multi-domain dependency maps and strategic templates',
        documentCount: 20,
        chunkCount: 75,
        lastIndexedAt: new Date().toISOString(),
      },
    ];

    for (const def of defaultDefs) {
      this.registerCollection(def);
      this.bindCollection({
        collectionId: def.id,
        ownerType: def.scopeType,
        ownerId: def.scopeId || 'system',
        allowedPrincipalTypes: ['member', 'admin', 'developer', 'sales', 'support', 'owner'],
        allowedChannelIds: ['*'], // default wildcard
        enabled: true,
      });
    }
  }

  registerCollection(def: RagCollectionDefinition): void {
    this.validateCollectionId(def.id, def.scopeType, def.scopeId);
    this.collections.set(def.id, def);
  }

  bindCollection(binding: RagCollectionBinding): void {
    this.bindings.set(binding.collectionId, binding);
  }

  getCollection(id: string): RagCollectionDefinition | undefined {
    return this.collections.get(id);
  }

  getBinding(id: string): RagCollectionBinding | undefined {
    return this.bindings.get(id);
  }

  listCollections(): readonly RagCollectionDefinition[] {
    return Array.from(this.collections.values());
  }

  listCollectionsByScope(scopeType: string, scopeId?: string): readonly RagCollectionDefinition[] {
    return Array.from(this.collections.values()).filter(
      (c) => c.scopeType === scopeType && (!scopeId || c.scopeId === scopeId)
    );
  }

  /**
   * Enforces URI namespacing rules:
   * - global:<name>
   * - plugin:<pluginId>:<name>
   * - channel:<channelId>:<name>
   */
  validateCollectionId(id: string, scopeType: string, scopeId?: string): void {
    const parts = id.split(':');
    if (parts.length < 2) {
      throw new Error(`Invalid RAG collection ID '${id}': must follow canonical URI format (e.g. global:name, plugin:id:name, channel:id:name)`);
    }

    const [prefix, p1, p2] = parts;
    if (prefix === 'global') {
      if (scopeType !== 'global') throw new Error(`Collection '${id}' has global prefix but scopeType is '${scopeType}'`);
    } else if (prefix === 'plugin') {
      if (scopeType !== 'plugin') throw new Error(`Collection '${id}' has plugin prefix but scopeType is '${scopeType}'`);
      if (scopeId && p1 !== scopeId) throw new Error(`Collection '${id}' pluginId '${p1}' does not match scopeId '${scopeId}'`);
    } else if (prefix === 'channel') {
      if (scopeType !== 'channel') throw new Error(`Collection '${id}' has channel prefix but scopeType is '${scopeType}'`);
      if (scopeId && p1 !== scopeId) throw new Error(`Collection '${id}' channelId '${p1}' does not match scopeId '${scopeId}'`);
    } else {
      throw new Error(`Invalid RAG collection prefix '${prefix}': must be 'global', 'plugin', or 'channel'`);
    }
  }

  /**
   * Validates if a channel is authorized to query a collection.
   */
  isChannelAuthorizedForCollection(channelId: string, collectionId: string): boolean {
    const binding = this.bindings.get(collectionId);
    if (!binding || !binding.enabled) return false;

    if (binding.allowedChannelIds.includes('*') || binding.allowedChannelIds.includes(channelId)) {
      return true;
    }

    return false;
  }
}
